import {
  OIDCLogin,
  OIDCLoginCallback,
  OIDCLogout,
  OIDCLogoutCallback,
  permissionsByEntity,
  PrivateRouteContent,
} from '@cmg/auth';
import { apiTypes, ApplicationError, urlUtil } from '@cmg/common';
import React from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { getAppSettings } from '../../common/config/appSettings';
import routeFactory from '../../common/util/routeFactory';
import DesignSystemAccountDetailRoute from '../design-system/account-detail/AccountDetailRoute';
import AccountListRoute from '../global-management/account-list/AccountListRoute';
import DraftAccountListRoute from '../global-management/draft-account-list/DraftAccountListRoute';
import TraitDetailRoute from '../global-management/trait-detail/TraitDetailRoute';
import TraitListRoute from '../global-management/trait-list/TraitListRoute';
import ConsentRoute from '../identity-management/consent/ConsentRoute';
import DesignSystemProfileRoute from '../identity-management/design-system/profile/ProfileRoute';
import { ErrorCode } from '../identity-management/error/constants';
import ErrorRoute from '../identity-management/error/ErrorRoute';
import FirstTimeActivationRoute from '../identity-management/first-time-activation/FirstTimeActivationRoute';
import ForgotPasswordRoute from '../identity-management/forgot-password/ForgotPasswordRoute';
import LoggedOutRoute from '../identity-management/loggedOut/LoggedOutRoute';
import LoginRoute from '../identity-management/login/LoginRoute';
import ResetPasswordRoute from '../identity-management/reset-password/ResetPasswordRoute';
import SelectDomainRoute from '../identity-management/select-domain/SelectDomainRoute';
import UnlockedRoute from '../identity-management/unlocked/UnlockedRoute';
import InvestorSignupRoute from '../investor-signup/InvestorSignupRoute';
import SuccessScreenRoute from '../investor-signup/success-screen/SuccessScreenRoute';
import { CRMManagementRoute } from '../rolodex/crm-management/CRMManagementRoute';
import RolodexRoute from '../rolodex/RolodexRoute';

type NoAccessProps = {
  isLoggedIn: boolean;
};

/**
 * NoAccessRedirect fallback redirect wrapper
 *
 * When the user is not logged in - redirect the user back
 * to the oidc login route url and set the location state to the current location.
 * When the user is logged in - redirect to the error route with forbidden error code
 */
export const NoAccessRedirect: React.FC<NoAccessProps> = ({ isLoggedIn }) => {
  const location = useLocation();

  // User is not logged in - redirect to login
  if (!isLoggedIn) {
    return (
      <Navigate
        to={routeFactory.oidcLogin.getUrlPath()}
        state={routeFactory.oidcLogin.getRouteState({
          location: location,
        })}
        replace
      />
    );
  }

  // User is logged in but does not have permissions to view this route - redirect to forbidden
  return (
    <Navigate
      to={routeFactory.error.getUrlPath({
        errorCode: ErrorCode.FORBIDDEN,
      })}
      replace
    />
  );
};

/**
 * Responsible for application level routes. Most routes in our sitemap should be represented here.
 */
export const RootRouter: React.FC = () => {
  const appSettings = getAppSettings();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Routes>
      {/* Identity */}
      <Route path={routeFactory.error.routePath} element={<ErrorRoute />} />
      <Route path={routeFactory.selectDomain.routePath} element={<SelectDomainRoute />} />
      <Route path={routeFactory.unlocked.routePath} element={<UnlockedRoute />} />
      <Route
        path={routeFactory.firstTimeActivation.routePath}
        element={<FirstTimeActivationRoute />}
      />
      <Route path={routeFactory.consent.routePath} element={<ConsentRoute />} />
      <Route path={routeFactory.login.routePath} element={<LoginRoute />} />
      <Route path={routeFactory.forgotPassword.routePath} element={<ForgotPasswordRoute />} />
      <Route path={routeFactory.resetPassword.routePath} element={<ResetPasswordRoute />} />
      <Route
        path={`${routeFactory.profile.routePath}/*`}
        element={
          <PrivateRouteContent
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <DesignSystemProfileRoute />
          </PrivateRouteContent>
        }
      />
      {/* Identity - Investor Signup */}
      {urlUtil.getAccountSubdomain(window.location) === 'signup' ? (
        <Route path={routeFactory.investorSignup.routePath} element={<InvestorSignupRoute />} />
      ) : (
        <Route
          path={routeFactory.investorSignup.routePath}
          element={
            <ApplicationError
              errorCode={apiTypes.ServiceErrorCode.NOT_FOUND}
              returnUrl={routeFactory.root.routePath}
            />
          }
        />
      )}
      {urlUtil.getAccountSubdomain(window.location) === 'signup' ? (
        <Route
          path={routeFactory.investorSignupSuccess.routePath}
          element={<SuccessScreenRoute />}
        />
      ) : (
        <Route
          path={routeFactory.investorSignupSuccess.routePath}
          element={
            <ApplicationError
              errorCode={apiTypes.ServiceErrorCode.NOT_FOUND}
              returnUrl={routeFactory.root.routePath}
            />
          }
        />
      )}
      {/* OIDC */}
      <Route path={routeFactory.loggedOut.routePath} element={<LoggedOutRoute />} />
      <Route
        path={routeFactory.oidcLogin.routePath}
        element={
          <OIDCLogin
            locationState={location.state}
            onError={() => {
              window.location.href =
                appSettings.client.basename + routeFactory.error.getUrlPath({});
            }}
          />
        }
      />
      <Route path={routeFactory.oidcLogout.routePath} element={<OIDCLogout />} />
      <Route
        path={routeFactory.oidcLoginCallback.routePath}
        element={
          <OIDCLoginCallback
            onSuccess={returnUrl => {
              navigate(returnUrl || routeFactory.profile.getUrlPath());
            }}
          />
        }
      />
      <Route
        path={routeFactory.oidcLogoutCallback.routePath}
        element={
          <OIDCLogoutCallback onSuccess={() => navigate(routeFactory.loggedOut.getUrlPath())} />
        }
      />
      {/* Global Management */}
      <Route
        path={`${routeFactory.adminAccounts.routePath}/*`}
        element={
          <PrivateRouteContent
            requiredPermissions={[permissionsByEntity.GlobalAccount.READ]}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <AccountListRoute />
          </PrivateRouteContent>
        }
      />
      <Route
        path={routeFactory.adminDraftAccounts.routePath}
        element={
          <PrivateRouteContent
            requiredPermissions={[permissionsByEntity.GlobalAccount.READ]}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <DraftAccountListRoute />
          </PrivateRouteContent>
        }
      />
      <Route
        path={
          // eslint-disable-next-line
          // FIXME: if the current subdomain isn't `system`, the required param `accountSubdomain` is missing
          `${routeFactory.accountDetail.routePath as ':accountSubdomain'}/*`
        }
        element={
          <PrivateRouteContent
            requiredPermissions={[
              permissionsByEntity.GlobalAccount.READ,
              permissionsByEntity.Account.READ,
            ]}
            requireAllPermissions={false}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <DesignSystemAccountDetailRoute />
          </PrivateRouteContent>
        }
      />
      <Route
        path={routeFactory.adminTraits.routePath}
        element={
          <PrivateRouteContent
            requiredPermissions={[permissionsByEntity.GlobalAccount.READ]}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <TraitListRoute />
          </PrivateRouteContent>
        }
      />
      <Route
        path={routeFactory.adminTraitDetail.routePath}
        element={
          <PrivateRouteContent
            requiredPermissions={[permissionsByEntity.GlobalAccount.READ]}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <TraitDetailRoute />
          </PrivateRouteContent>
        }
      />

      {/* Rolodex */}
      <Route
        path={`${routeFactory.rolodex.routePath}/*`}
        element={
          <PrivateRouteContent
            requiredPermissions={[permissionsByEntity.Firm.READ]}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <RolodexRoute />
          </PrivateRouteContent>
        }
      />
      {/* Customer Facing CRM Management */}
      <Route
        path={`${routeFactory.customerCrmManagement.routePath}/*`}
        element={
          <PrivateRouteContent
            requiredPermissions={[permissionsByEntity.CRM.FULL]}
            renderNoAccess={isLoggedIn => <NoAccessRedirect isLoggedIn={isLoggedIn} />}
          >
            <CRMManagementRoute />
          </PrivateRouteContent>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to={routeFactory.profile.getUrlPath()} replace />} />
    </Routes>
  );
};

export default RootRouter;
